/**
 * Module Terminology Utility
 * 
 * Provides consistent terminology for the 3-level module hierarchy:
 * Level 1: Module
 * Level 2: Component 
 * Level 3: Subcomponent
 */

// Define the hierarchy mapping
const LEVEL_TERMINOLOGY = {
  1: {
    singular: 'Module',
    plural: 'Modules',
    article: 'a',
  },
  2: {
    singular: 'Component',
    plural: 'Components',
    article: 'a',
  },
  3: {
    singular: 'Subcomponent',
    plural: 'Subcomponents',
    article: 'a',
  },
};

/**
 * Get terminology for a specific level
 * @param {number} level - The level (1, 2, or 3)
 * @returns {object} Terminology object with singular, plural, and article
 */
export const getTerminologyForLevel = (level) => {
  return LEVEL_TERMINOLOGY[level] || LEVEL_TERMINOLOGY[1];
};

/**
 * Get the terminology for what will be created when adding to a parent level
 * @param {number} parentLevel - The level of the parent node
 * @returns {object} Terminology for the child level
 */
export const getChildTerminology = (parentLevel) => {
  const childLevel = parentLevel + 1;
  return getTerminologyForLevel(childLevel);
};

/**
 * Generate add button text based on parent level
 * @param {number} parentLevel - The level of the parent node
 * @returns {object} Object with name and description for the add button
 */
export const getAddButtonText = (parentLevel) => {
  const childTerms = getChildTerminology(parentLevel);
  
  return {
    name: `Add ${childTerms.singular}`,
    description: `Create new ${childTerms.singular.toLowerCase()}`,
  };
};

/**
 * Generate modal title for adding based on parent level
 * @param {number} parentLevel - The level of the parent node
 * @returns {string} Modal title
 */
export const getAddModalTitle = (parentLevel) => {
  const childTerms = getChildTerminology(parentLevel);
  return `Add ${childTerms.singular}`;
};

/**
 * Generate modal title for editing based on node level
 * @param {number} nodeLevel - The level of the node being edited
 * @returns {string} Edit modal title
 */
export const getEditModalTitle = (nodeLevel) => {
  const currentTerms = getTerminologyForLevel(nodeLevel);
  return `Edit ${currentTerms.singular}`;
};

/**
 * Get cascade text for module delete (what else gets deleted)
 * @param {number} nodeLevel - The level of the node being deleted
 * @returns {string} Cascade description (suffix for getDeleteConfirmMessage)
 */
export const getDeleteCascadeText = (nodeLevel) => {
  const deletedItems = [];
  for (let level = nodeLevel + 1; level <= 3; level++) {
    const terms = getTerminologyForLevel(level);
    deletedItems.push(terms.plural.toLowerCase());
  }
  deletedItems.push('associated test cases');
  if (deletedItems.length === 1) {
    return `It will delete all ${deletedItems[0]}.`;
  }
  const lastItem = deletedItems.pop();
  const itemsList = deletedItems.join(', ') + ` and ${lastItem}`;
  return `This action will delete all ${itemsList}.`;
};

/**
 * Check if a level can have children (level < 3)
 * @param {number} level - The level to check
 * @returns {boolean} True if the level can have children
 */
export const canHaveChildren = (level) => {
  return level < 3;
};

/**
 * Get all terminology mappings (useful for debugging or display)
 * @returns {object} All level terminology
 */
export const getAllTerminology = () => {
  return LEVEL_TERMINOLOGY;
};
